// Asset Management Type Definitions

export enum AssetScope {
  BUILDING = 'building',
  UNIT = 'unit',
  ROOM = 'room',
  PORTABLE = 'portable',
}

export enum AssetStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  MAINTENANCE = 'maintenance',
  DEFECT = 'defect',
  RETIRED = 'retired',
  DECOMMISSIONED = 'decommissioned',
  PENDING_INSTALLATION = 'pending_installation'
}

export enum FieldType {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  DATE = 'date',
  ENUM = 'enum',
  MULTI_ENUM = 'multi_enum',
  COMPANY_REFERENCE = 'company_reference',
  FILE_REFERENCE = 'file_reference',
}

export enum RequirementType {
  REQUIRED = 'REQUIRED',
  RECOMMENDED = 'RECOMMENDED',
  OPTIONAL = 'OPTIONAL',
}

export enum LogicalOperator {
  AND = 'AND',
  OR = 'OR',
}

export enum ConditionOperator {
  EQUALS = 'equals',
  NOT_EQUALS = 'not_equals',
  GREATER_THAN = 'greater_than',
  LESS_THAN = 'less_than',
  GREATER_THAN_OR_EQUAL = 'greater_than_or_equal',
  LESS_THAN_OR_EQUAL = 'less_than_or_equal',
  CONTAINS = 'contains',
  NOT_CONTAINS = 'not_contains',
  IN = 'in',
  NOT_IN = 'not_in',
  IS_NULL = 'is_null',
  IS_NOT_NULL = 'is_not_null',
}

// Validation rules for field definitions
export interface ValidationRule {
  min?: number;
  max?: number;
  pattern?: string;
  required?: boolean;
  customValidator?: string;
}

// Metadata for field definitions
export interface FieldMetadata {
  unit?: string;
  placeholder?: string;
  helpText?: string;
  icon?: string;
  suffix?: string;
}

// File-log connection configuration for field definitions
export interface FileLogConnection {
  enabled: boolean;
  sourceField: 'documentDate' | 'expirationDate' | 'createdAt';
  priorityStrategy: 'NEWEST_CREATED' | 'OLDEST_CREATED' | 'NEWEST_DOCUMENT_DATE' | 'OLDEST_DOCUMENT_DATE' | 'NEWEST_EXPIRATION' | 'OLDEST_EXPIRATION';
  fileLabelFilter?: string[];  // Only pull from files with these labels (optional)
}

// Field definition for asset categories
export interface FieldDefinition {
  id?: string;
  assetCategoryId?: string;
  fieldName: string;
  displayName: string;
  fieldType: FieldType;
  isRequired?: boolean;
  displayOrder: number;
  validationRules?: ValidationRule;
  enumValues?: string[];
  metadata?: FieldMetadata;
  fileLogConnection?: FileLogConnection;
  /** Maps enum values to specific asset type IDs. If empty/null = available for all types. */
  enumTypeMapping?: Record<string, number[]> | null;
  isActive?: boolean;
  showInTable?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

// Type-level field definition (specific to asset type)
export interface AssetTypeFieldDefinition {
  id: number;
  assetTypeId: number;
  fieldName: string;
  displayName: string;
  fieldType: FieldType;
  isRequired: boolean;
  defaultValue?: any;
  validationRules?: ValidationRule;
  displayOrder: number;
  enumValues?: string[];
  metadata?: FieldMetadata;
  fileLogConnection?: FileLogConnection;
  active: boolean;
  showInTable?: boolean;
}

// Asset Type Supplier Mapping
export interface AssetTypeSupplierMapping {
  id: number;
  assetTypeId: number;
  supplierId: number;
  supplier?: { id: number; name: string };
  conditions?: TemplateConditions | null;
  segmentId?: number | null;
  segment?: { id: number; name: string };
  createdAt: string;
  updatedAt: string;
}

export interface CreateSupplierMappingDto {
  supplierId: number;
  conditions?: TemplateConditions | null;
  segmentId?: number | null;
}

export interface UpdateSupplierMappingDto {
  supplierId?: number;
  conditions?: TemplateConditions | null;
  segmentId?: number | null;
}

// Asset Type
export interface AssetType {
  id: number;
  assetCategoryId: number;
  name: string;
  description?: string;
  icon?: string;
  scopes: AssetScope[];
  displayOrder: number;
  scopeRequirements?: Record<string, 'required' | 'optional'> | null;
  scopeOverrides?: {
    includeBuildingIds?: string[];
    excludeBuildingIds?: string[];
    includeUnitIds?: string[];
    excludeUnitIds?: string[];
  } | null;
  isSystemManaged?: boolean;
  active: boolean;
  fieldDefinitions?: AssetTypeFieldDefinition[];
  fileRequirements?: FileRequirement[];
  maintenanceTemplates?: MaintenanceScheduleTemplate[];
  supplierMappings?: AssetTypeSupplierMapping[];
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
  updatedBy?: string;
}

// Asset category
export interface AssetCategory {
  id: number;
  name: string;
  description?: string;
  icon?: string;
  displayOrder: number;
  active: boolean;
  isSystemManaged?: boolean;
  color?: string;
  slug?: string | null;
  fieldDefinitions?: AssetFieldDefinition[];
  assetTypes?: AssetType[];
  createdAt: Date;
  updatedAt: Date;
}

// Asset field definition
export interface AssetFieldDefinition {
  id: number;
  assetCategoryId: number;
  fieldName: string;
  displayName: string;
  fieldType: FieldType;
  isRequired: boolean;
  defaultValue?: any;
  validationRules?: ValidationRule;
  displayOrder: number;
  enumValues?: string[];
  metadata?: FieldMetadata;
  fileLogConnection?: FileLogConnection;
  enumTypeMapping?: Record<string, number[]> | null;
  active: boolean;
  showInTable?: boolean;
}

// Dynamic field value for assets
export interface FieldValue {
  id?: string;
  assetId?: string;
  fieldDefinitionId: string;
  fieldKey: string;
  value: any;
  createdAt?: Date;
  updatedAt?: Date;
}

// Asset
export interface Asset {
  id: string;
  assetCategoryId: number;
  assetCategory?: AssetCategory;
  assetTypeId: number;
  assetType?: AssetType;
  name: string;
  description?: string;
  identifier?: string;
  buildingId?: string;
  building?: {
    id: string;
    company?: { name: string };
  };
  unitId?: string;
  unit?: {
    id: string;
    buildingId: string;
  };
  roomId?: number;
  room?: {
    id: number;
    roomType?: string;
    name?: string;
  };
  location?: string;
  installerId?: number;
  installer?: {
    id: number;
    name: string;
  };
  maintenanceCompanyId?: number;
  maintenanceCompany?: {
    id: number;
    name: string;
  };
  parentAssetId?: string;
  parentAsset?: { id: string; name: string };
  status: AssetStatus;
  fieldValues?: Record<string, any>;
  installationDate?: Date;
  warrantyExpiryDate?: Date;
  lastMaintenanceDate?: Date;
  nextMaintenanceDate?: Date;
  notes?: string;
  archived: boolean;
  hasMaintenanceSchedule?: boolean;
  nextUnvalidatedEventDate?: string | null;
  lastMaintenanceValidated?: boolean | null;
  shortId?: number;
  sharePointFolderId?: string;
  sharePointFolderUrl?: string;
  createdAt: Date;
  updatedAt: Date;
}

// Condition for file requirements
export interface Condition {
  id?: string;
  entityPath: string;
  operator: ConditionOperator;
  value: any;
  logicalOperator?: LogicalOperator;
}

// Condition group for nested conditions
export interface ConditionGroup {
  id?: string;
  logicalOperator: LogicalOperator;
  conditions?: Condition[];
  groups?: ConditionGroup[];
}

// File requirement for asset types
export interface FileRequirement {
  id: string;
  assetTypeId: number;
  assetType?: AssetType;
  fileLabelId: string;
  fileLabel?: {
    id: string;
    label: string;
  };
  requirementType: RequirementType;
  description?: string;
  conditionGroup?: ConditionGroup;
  scopes?: AssetScope[] | null;
  active: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Entity metadata for condition builder
export interface EntityField {
  key: string;
  displayName: string;
  fieldType: FieldType;
  enumValues?: string[];
  isRelation?: boolean;
  relationEntity?: string;
}

export interface EntityMetadata {
  entityName: string;
  displayName: string;
  fields: EntityField[];
  relations?: {
    [key: string]: EntityMetadata;
  };
}

// API request/response types
export interface CreateAssetCategoryDto {
  name: string;
  description?: string;
  icon?: string;
  displayOrder?: number;
}

export interface UpdateAssetCategoryDto {
  name?: string;
  description?: string;
  icon?: string;
  displayOrder?: number;
  active?: boolean;
  slug?: string | null;
}

export interface CreateFieldDefinitionDto {
  assetCategoryId: string;
  fieldName: string;
  displayName: string;
  fieldType: FieldType;
  isRequired?: boolean;
  displayOrder: number;
  validationRules?: ValidationRule;
  enumValues?: string[];
  metadata?: FieldMetadata;
  fileLogConnection?: FileLogConnection;
  showInTable?: boolean;
  enumTypeMapping?: Record<string, number[]> | null;
}

export interface UpdateFieldDefinitionDto {
  fieldName?: string;
  displayName?: string;
  fieldType?: FieldType;
  isRequired?: boolean;
  displayOrder?: number;
  validationRules?: ValidationRule;
  enumValues?: string[];
  metadata?: FieldMetadata;
  fileLogConnection?: FileLogConnection;
  active?: boolean;
  showInTable?: boolean;
  enumTypeMapping?: Record<string, number[]> | null;
}

export interface CreateAssetTypeDto {
  assetCategoryId: number;
  name: string;
  description?: string;
  icon?: string;
  scopes?: AssetScope[];
  displayOrder?: number;
  scopeRequirements?: Record<string, 'required' | 'optional'> | null;
  scopeOverrides?: {
    excludeBuildings?: { id: string; reason?: string }[];
    excludeUnits?: { id: string; reason?: string }[];
    includeBuildings?: { id: string; reason?: string }[];
    includeUnits?: { id: string; reason?: string }[];
  } | null;
}

export interface UpdateAssetTypeDto {
  name?: string;
  description?: string;
  icon?: string;
  scopes?: AssetScope[];
  displayOrder?: number;
  scopeRequirements?: Record<string, 'required' | 'optional'> | null;
  scopeOverrides?: {
    excludeBuildings?: { id: string; reason?: string }[];
    excludeUnits?: { id: string; reason?: string }[];
    includeBuildings?: { id: string; reason?: string }[];
    includeUnits?: { id: string; reason?: string }[];
  } | null;
  active?: boolean;
}

export interface CreateAssetDto {
  id: string;
  assetCategoryId: number;
  assetTypeId?: number; // TODO: Make required when assetType selection is implemented in AssetForm
  name: string;
  description?: string;
  identifier?: string;
  buildingId?: string;
  unitId?: string;
  roomId?: number;
  location?: string;
  status?: AssetStatus;
  fieldValues?: Record<string, any>;
  installationDate?: Date;
  warrantyExpiryDate?: Date;
  lastMaintenanceDate?: Date;
  nextMaintenanceDate?: Date;
  notes?: string;
  archived: boolean;
}

export interface UpdateAssetDto {
  assetCategoryId: number;
  assetTypeId?: number;
  parentAssetId?: string | null;
  name?: string;
  description?: string;
  identifier?: string;
  buildingId?: string;
  unitId?: string;
  roomId?: number;
  location?: string;
  status?: AssetStatus;
  fieldValues?: Record<string, any>;
  installationDate?: Date;
  warrantyExpiryDate?: Date;
  lastMaintenanceDate?: Date;
  nextMaintenanceDate?: Date;
  notes?: string;
}

export interface CreateFileRequirementDto {
  assetTypeId: number;
  fileLabelId: string;
  requirementType: RequirementType;
  description?: string;
  conditionGroup?: ConditionGroup;
  scopes?: AssetScope[] | null;
}

// Type-level field definition DTOs
export interface CreateTypeFieldDefinitionDto {
  fieldName: string;
  displayName: string;
  fieldType: FieldType;
  isRequired?: boolean;
  defaultValue?: any;
  validationRules?: ValidationRule;
  displayOrder?: number;
  enumValues?: string[];
  metadata?: FieldMetadata;
  fileLogConnection?: FileLogConnection;
  showInTable?: boolean;
}

export interface UpdateTypeFieldDefinitionDto {
  fieldName?: string;
  displayName?: string;
  fieldType?: FieldType;
  isRequired?: boolean;
  defaultValue?: any;
  validationRules?: ValidationRule;
  displayOrder?: number;
  enumValues?: string[];
  metadata?: FieldMetadata;
  fileLogConnection?: FileLogConnection;
  active?: boolean;
  showInTable?: boolean;
}

export interface UpdateFileRequirementDto {
  fileLabelId?: string;
  requirementType?: RequirementType;
  description?: string;
  conditionGroup?: ConditionGroup;
  scopes?: AssetScope[] | null;
  active?: boolean;
}

export enum MaintenanceType {
  CRITICAL = 'critical',
  PREVENTIVE = 'preventive',
}

export const MaintenanceTypeLabels: Record<MaintenanceType, string> = {
  [MaintenanceType.CRITICAL]: 'Kritiek',
  [MaintenanceType.PREVENTIVE]: 'Preventief',
};

export enum MaintenanceResponsible {
  TENANT = 'tenant',
  YALLY = 'yally',
  OWNER = 'owner',
}

export const MaintenanceResponsibleLabels: Record<MaintenanceResponsible, string> = {
  [MaintenanceResponsible.TENANT]: 'Huurder',
  [MaintenanceResponsible.YALLY]: 'Yally',
  [MaintenanceResponsible.OWNER]: 'Eigenaar',
};

export enum MaintenanceFrequency {
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  BIANNUAL = 'biannual',
  YEARLY = 'yearly',
  TWO_YEARLY = 'two_yearly',
  FIVE_YEARLY = 'five_yearly',
  CUSTOM = 'custom',
}

export const MaintenanceFrequencyLabels: Record<MaintenanceFrequency, string> = {
  [MaintenanceFrequency.MONTHLY]: 'Maandelijks',
  [MaintenanceFrequency.QUARTERLY]: 'Per kwartaal',
  [MaintenanceFrequency.BIANNUAL]: 'Halfjaarlijks',
  [MaintenanceFrequency.YEARLY]: 'Jaarlijks',
  [MaintenanceFrequency.TWO_YEARLY]: 'Tweejaarlijks',
  [MaintenanceFrequency.FIVE_YEARLY]: 'Vijfjaarlijks',
  [MaintenanceFrequency.CUSTOM]: 'Aangepast',
};

export interface AssetMaintenanceSchedule {
  id: number;
  assetId: string;
  asset?: Asset;
  name: string;
  description?: string;
  frequency: MaintenanceFrequency;
  customFrequencyMonths?: number;
  firstDueDate: string;
  lastCompletedDate?: string;
  nextDueDate?: string;
  reminderDaysBefore?: number;
  supplierId?: number;
  supplier?: { id: number; name: string };
  assignedTo?: string;
  estimatedCost?: number;
  notes?: string;
  maintenanceType: MaintenanceType;
  responsibleParty?: MaintenanceResponsible;
  publicToken?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export enum MaintenanceEventStatus {
  OPEN = 'open',
  REPORTED = 'reported',
  VALIDATED = 'validated',
}

export const MaintenanceEventStatusLabels: Record<MaintenanceEventStatus, string> = {
  [MaintenanceEventStatus.OPEN]: 'Openstaand',
  [MaintenanceEventStatus.REPORTED]: 'Gerapporteerd',
  [MaintenanceEventStatus.VALIDATED]: 'Gevalideerd',
};

export interface MaintenanceEventFile {
  id: number;
  maintenanceEventId: number;
  fileLogId: number;
  fileLog?: {
    id: number;
    label?: string[];
    sharePointFileId?: string;
    sharePointFileUrl?: string;
    summary?: string;
    createdAt: string;
  };
  description?: string;
  createdBy?: string;
  createdAt: string;
}

export interface MaintenanceEvent {
  id: number;
  scheduleId: number;
  schedule?: AssetMaintenanceSchedule;
  assetId: string;
  asset?: Asset;
  dueDate: string;
  completedDate?: string;
  status: MaintenanceEventStatus;
  summary?: string;
  supplierReport?: string;
  reporterName?: string;
  reporterEmail?: string;
  reporterPhone?: string;
  validatedBy?: string;
  /** @deprecated Use files via GET /maintenance-events/:id/files */
  reportFilePath?: string;
  /** @deprecated Use files via GET /maintenance-events/:id/files */
  sharePointFileId?: string;
  /** @deprecated Use files via GET /maintenance-events/:id/files */
  sharePointFileUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMaintenanceScheduleDto {
  name: string;
  description?: string;
  frequency: MaintenanceFrequency;
  customFrequencyMonths?: number;
  firstDueDate?: string;
  lastCompletedDate?: string;
  maintenanceType?: MaintenanceType;
  responsibleParty?: MaintenanceResponsible;
  reminderDaysBefore?: number;
  supplierId?: number;
  assignedTo?: string;
  estimatedCost?: number;
  notes?: string;
}

export interface UpdateMaintenanceScheduleDto {
  name?: string;
  description?: string;
  frequency?: MaintenanceFrequency;
  customFrequencyMonths?: number;
  firstDueDate?: string;
  lastCompletedDate?: string;
  maintenanceType?: MaintenanceType;
  responsibleParty?: MaintenanceResponsible;
  reminderDaysBefore?: number;
  supplierId?: number;
  assignedTo?: string;
  estimatedCost?: number;
  notes?: string;
  active?: boolean;
}

// Maintenance Schedule Template types
export interface TemplateConditionRule {
  type: 'cluster' | 'clusterGroup' | string;
  operator: 'in' | 'not_in' | 'equals' | 'not_equals' | 'gt' | 'gte' | 'lt' | 'lte';
  value: any;
}

export interface TemplateConditions {
  logicalOperator: 'AND' | 'OR';
  rules: TemplateConditionRule[];
}

export interface MaintenanceScheduleTemplate {
  id: number;
  assetTypeId: number;
  name: string;
  description?: string;
  frequency: MaintenanceFrequency;
  customFrequencyMonths?: number;
  maintenanceType: MaintenanceType;
  responsibleParty?: MaintenanceResponsible;
  reminderDaysBefore?: number;
  estimatedCost?: number;
  notes?: string;
  active: boolean;
  conditions?: TemplateConditions | null;
  segmentId?: number | null;
  segment?: { id: number; name: string };
  createdAt: string;
  updatedAt: string;
}

export interface CreateMaintenanceTemplateDto {
  name: string;
  description?: string;
  frequency: MaintenanceFrequency;
  customFrequencyMonths?: number;
  maintenanceType?: MaintenanceType;
  responsibleParty?: MaintenanceResponsible;
  reminderDaysBefore?: number;
  estimatedCost?: number;
  notes?: string;
  active?: boolean;
  conditions?: TemplateConditions | null;
  segmentId?: number | null;
}

export interface UpdateMaintenanceTemplateDto {
  name?: string;
  description?: string;
  frequency?: MaintenanceFrequency;
  customFrequencyMonths?: number;
  maintenanceType?: MaintenanceType;
  responsibleParty?: MaintenanceResponsible;
  reminderDaysBefore?: number;
  estimatedCost?: number;
  notes?: string;
  active?: boolean;
  conditions?: TemplateConditions | null;
  segmentId?: number | null;
}

// Response from asset creation — may include template conflicts requiring user resolution
export interface CreateAssetResponse extends Asset {
  maintenanceTemplateConflicts?: MaintenanceScheduleTemplate[];
}

// Response from completing (validating) a maintenance event
export interface CompleteMaintenanceEventResponse {
  event: MaintenanceEvent;
  proposedNextDueDate: string;
}

export interface AssetFilters {
  assetCategoryId?: string;
  assetTypeId?: string;
  buildingId?: string;
  unitId?: string;
  roomId?: number;
  status?: string;
  search?: string;
}

export interface PaginatedAssetsResponse {
  data: Asset[];
  totalCount: number;
  hasMore: boolean;
  categoryCounts: { id: number; name: string; count: number }[];
  typeCounts: { id: number; name: string; icon?: string; count: number }[];
}

export interface EvaluatedFileRequirement {
  requirement: FileRequirement;
  isRequired: boolean;
  reason?: string;
}
